import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import OverviewMetricBarGraphPage from "../pages/OverviewMetricBarGraphPage/OverviewMetricBarGraphPage";
import RMRCheatsheetPage from "../pages/RMRCheatsheet/RMRCheatsheetPage";
import { SvgConverterType } from "./Report";
import { useElevenLabsWidget } from "../hooks/useElevenLabsWidget";
import ReportNotFound from "./ReportNotFound";
import BiologicalAgePage from "../pages/BiologicalAgePageOld/BiologicalAgePage";
import { getSelectedAgentTypes } from "../../../providers/helperFunctions";
import {
  ChatRoot,
  resolveChatProviderMode,
} from "../../chat";

type Props = {
  data: any;
  svgConverter: SvgConverterType;
  reportType: string;
  isCreatingPDF: boolean;
  reportId?: string;
};

const ReportRMRCheatsheet = ({
  data,
  svgConverter,
  reportType,
  isCreatingPDF,
  reportId,
}: Props) => {
  const location = useLocation();
  const agentTypes = getSelectedAgentTypes(data.report_agent);
  const agentType = agentTypes.includes("Cheatsheet")
    ? "Cheatsheet"
    : undefined;
  const chatProviderMode = resolveChatProviderMode(location.search);
  const isMockChatMode = chatProviderMode === "mock";
  const isElevenLabsMode = !isMockChatMode;
  const chatSurfaceId = `cheatsheet:${reportId || "unknown"}`;

  // Always call the hook - pass agent type string
  useElevenLabsWidget(
    reportId,
    agentType,
    undefined,
    undefined,
    isElevenLabsMode
  );

  // Force a layout recalculation after all resources are loaded
  useEffect(() => {
    // Wait for the window load event to ensure all resources are available
    const handleLoad = () => {
      // Force a layout recalculation by triggering a resize event
      window.dispatchEvent(new Event("resize"));
    };

    // If the window is already loaded, dispatch the event immediately
    if (document.readyState === "complete") {
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 500);
    } else {
      window.addEventListener("load", handleLoad);
      return () => {
        window.removeEventListener("load", handleLoad);
      };
    }
  }, []);

  if (
    data.customType !== "PNOE_tester" &&
    data.customType !== "restore" &&
    data.customType !== "optimizeu" &&
    data.customType !== "thedripbar"
  ) {
    return <ReportNotFound error={"Not authorized"} />;
  }

  // delete when deployed new VO2 max chart
  const biaxialLineChartDataHandler = () => {
    const biaxialLineChartData = {
      gender: data.GENDER,
      lines_men: [
        {
          x: 15,
          area0: [15, 37],
          area1: [37, 42],
          area2: [42, 47],
          area3: [47, 52],
          area4: [52, 65],
        },
        {
          x: 75,
          area0: [15, 21],
          area1: [21, 24],
          area2: [24, 27],
          area3: [27, 30],
          area4: [30, 65],
        },
        { x: data.AGE, lineUser: data.VO2PEAK },
      ],
      lines_women: [
        {
          x: 15,
          area0: [15, 33],
          area1: [33, 38],
          area2: [38, 42],
          area3: [42, 47],
          area4: [47, 65],
        },
        {
          x: 75,
          area0: [15, 17],
          area1: [17, 20],
          area2: [20, 23],
          area3: [23, 25],
          area4: [25, 65],
        },
        { x: data.AGE, lineUser: data.VO2PEAK },
      ],
    };
    return biaxialLineChartData;
  };

  return (
    <>
      {isElevenLabsMode && <div id="chat-container"></div>}
      {isMockChatMode && (
        <ChatRoot
          surfaceId={chatSurfaceId}
          providerMode={chatProviderMode}
        />
      )}

      <div>
        <OverviewMetricBarGraphPage
          data={data.PAGES.find((page: any) => page.PAGE_TYPE === "OVERVIEW")}
          onSvgConverter={svgConverter}
          reportType={
            reportType === "10" || reportType === "12" || reportType === "14"
              ? "isAMR"
              : reportType === "18"
              ? "isFrank_AMR"
              : reportType === "17"
              ? "isFrank_RMR"
              : reportType === "19"
              ? "isRMRCheatsheet"
              : "isRMR"
          }
          customReportType={data.customType}
          isCreatingPDF={isCreatingPDF}
          affiliateName={
            data.PAGES.find((page: any) => (page.PAGE_TYPE = "COVER"))
              .AFFILIATE_NAME
          }
          costumerName={
            data.PAGES.find((page: any) => (page.PAGE_TYPE = "COVER"))
              .SUBJECT_NAME
          }
          testDateLabel={
            data.PAGES.find((page: any) => (page.PAGE_TYPE = "COVER"))
              .TEST_DATE_LABEL
          }
          testDate={
            data.PAGES.find((page: any) => (page.PAGE_TYPE = "COVER")).DATE
          }
        />
        {data.customType === "thedripbar" &&
          data.PAGES.find(
            (page: any) => page.PAGE_TYPE === "BIOLOGICAL_AGE"
          ) && (
            <BiologicalAgePage
              data={data.PAGES.find(
                (page: any) => page.PAGE_TYPE === "BIOLOGICAL_AGE"
              )}
              onSvgConverter={svgConverter}
              biaxialLineChartData={biaxialLineChartDataHandler()}
              dataNew={
                data.PAGES.find((page: any) => page.PAGE_TYPE === "OVERVIEW")
                  .NAVBAR
              }
              showVO2MaxChart={true}
              isDripBarReport={data.customType === "thedripbar"}
            />
          )}
        <RMRCheatsheetPage
          data={
            data.PAGES.find((page: any) => page.PAGE_TYPE === "CHEAT_SHEET")
              .DATA
          }
          customReportType={data.customType}
          reportType={reportType}
        />
      </div>
    </>
  );
};

export default ReportRMRCheatsheet;
